import React from 'react';

const CHUNK_ERROR_PATTERN = /ChunkLoadError|Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i;
const CHUNK_RELOAD_KEY = 'overpos_chunk_reload_at';
const ERROR_LOG_KEY = 'overpos_client_errors';
const CHUNK_RELOAD_WINDOW_MS = 30000;

function createErrorId() {
  return `ERR-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorId: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, errorId: createErrorId() };
  }

  componentDidMount() {
    this.clearChunkReloadTimer = window.setTimeout(() => {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    }, CHUNK_RELOAD_WINDOW_MS);
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, errorId: null });
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.clearChunkReloadTimer);
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught render error:', error, errorInfo);

    try {
      const previous = JSON.parse(localStorage.getItem(ERROR_LOG_KEY) || '[]');
      const entry = {
        id: this.state.errorId,
        timestamp: new Date().toISOString(),
        message: error?.message || String(error),
        stack: error?.stack || null,
        componentStack: errorInfo?.componentStack || null,
        url: window.location.href,
        userAgent: navigator.userAgent
      };
      localStorage.setItem(ERROR_LOG_KEY, JSON.stringify([entry, ...previous].slice(0, 10)));
    } catch (loggingError) {
      console.warn('[ErrorBoundary] Could not persist client diagnostic:', loggingError);
    }

    const message = `${error?.name || ''} ${error?.message || ''}`;
    if (CHUNK_ERROR_PATTERN.test(message)) {
      const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
      if (!lastReloadAt || Date.now() - lastReloadAt > CHUNK_RELOAD_WINDOW_MS) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
        window.location.reload();
      }
    }
  }

  resetBoundary = () => {
    this.setState({ hasError: false, error: null, errorId: null });
  };

  render() {
    if (this.state.hasError) {
      const compact = this.props.compact === true;
      return (
        <div style={{
          minHeight: compact ? '320px' : '100vh',
          height: compact ? '100%' : '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          padding: '24px',
          textAlign: 'center',
          fontFamily: 'Inter, sans-serif',
          background: '#0f172a',
          color: '#e2e8f0',
          borderRadius: compact ? '12px' : 0,
        }}>
          <h1 style={{ margin: 0, fontSize: '22px' }}>
            {compact ? 'This section could not be displayed' : 'Something went wrong'}
          </h1>
          <p style={{ margin: 0, maxWidth: '520px', color: '#94a3b8' }}>
            Your data is safe. Try the section again, or reload the application if the problem continues.
          </p>
          <p style={{ margin: 0, color: '#64748b', fontSize: '12px' }}>
            Reference: {this.state.errorId}
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={this.resetBoundary}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: '1px solid #475569',
                background: '#1e293b',
                color: '#e2e8f0',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: '#22c55e',
                color: '#052e16',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
